import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "../../components/page-header/page-header";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { useGet } from "../../packages/hooks";
import { Product } from "../../types";
import {
  formatDiscountTimeRemaining,
  getDiscountTimeRemaining,
} from "../../utils/discounts";
import "./product-details.css";
import { getDiscountedRentalPriceText } from "./utils/products-utils";

const ProductDetails: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("");

  // Update time remaining for active discounts
  useEffect(() => {
    if (!product || !product.rental?.hasDiscount) {
      setTimeRemaining("");
      return;
    }

    const updateTimeRemaining = () => {
      const discountCreatedAt = product.rental.discountCreatedAt;
      const discountDuration = product.rental.discountDuration;
      const discountType = product.rental.discountType || "DAYS";

      // If discount data is missing, skip time calculation
      if (!discountCreatedAt || !discountDuration) {
        setTimeRemaining("");
        return;
      }

      const remaining = getDiscountTimeRemaining(
        discountCreatedAt,
        discountDuration,
        discountType
      );

      setTimeRemaining(formatDiscountTimeRemaining(remaining));
    };

    // Update immediately
    updateTimeRemaining();

    // Update every second
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [product]);

  // Fetch product details with proper error handling
  const productQuery = useGet<Product>(
    productId ? API_ENDPOINTS.PRODUCTS.BY_ID(productId) : undefined,
    {
      onSuccess: (data) => {
        // Map API response to Product interface
        const productData = data?.data || data;
        const mappedProduct: Product = {
          id: productData.id,
          lotNumber: productData.lotNumber || productData.name || "N/A",
          brand:
            productData.brand?.name ||
            productData.name?.split(" ")[0] ||
            "Unknown",
          model:
            productData.model?.name ||
            productData.name?.split(" ")[1] ||
            "Unknown",
          year: productData.year,
          mileage: productData.mileage,
          fuelType: productData.fuelType,
          transmission: productData.transmission,
          drivetrain: productData.drivetrain,
          engineSize: productData.engineSize,
          licensePlate: productData.licensePlate || "N/A",
          color: productData.color || "N/A",
          price: productData.price || productData.originalPrice || "0",
          rental: productData.rental || {
            status: productData.status || "ACTIVE",
            rentalPeriod: "DAY",
            price: parseFloat(productData.price || "0"),
            hasDiscount: false,
            discountType: undefined,
            discountDuration: 0,
            discountCreatedAt: null,
          },
          status: productData.status || "ACTIVE",
          images: productData.images || [],
          mainImage: productData.mainImage || "",
          description: productData.description || "",
          order: productData.order || 0,
          meta: productData.meta || {
            title: productData.metaTitle || productData.name || "",
            description:
              productData.metaDescription || productData.description || "",
            keywords: productData.metaKeywords || "",
          },
          isActive: productData.isActive,
          isFeatured: productData.isFeatured,
          createdAt: productData.createdAt,
          updatedAt: productData.updatedAt,
        };

        setProduct(mappedProduct);
        setIsLoading(false);
        setError(null);
      },
      onError: (error) => {
        console.error("Product fetch error:", error);
        setError("Ошибка при загрузке продукта");
        setIsLoading(false);
        toast.error("Ошибка при загрузке продукта");
      },
      enabled: !!productId,
    }
  );

  useEffect(() => {
    if (!productId) {
      setError("ID продукта не найден");
      setIsLoading(false);
      return;
    }

    // The useGet hook will automatically fetch when enabled
    setIsLoading(productQuery.isLoading);

    if (productQuery.isError) {
      setError("Ошибка при загрузке данных");
      setIsLoading(false);
    }
  }, [productId, productQuery.isLoading, productQuery.isError]);

  const formatMileage = (mileage: number): string => {
    return new Intl.NumberFormat("ru-RU").format(mileage) + " км";
  };

  if (productQuery.isLoading || isLoading) {
    return (
      <div className="product-details">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных продукта...</p>
        </div>
      </div>
    );
  }

  if (productQuery.isError || error || !product) {
    return (
      <div className="product-details">
        <div className="error-container">
          <h2>Ошибка загрузки</h2>
          <p>{error || productQuery.error?.message || "Продукт не найден"}</p>
          <div className="error-actions">
            <button
              className="btn btn-primary"
              onClick={() => productQuery.refetch()}
            >
              Попробовать снова
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/products")}
            >
              Вернуться к списку
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details">
      <PageHeader
        title={``}
        showBackButton={true}
        backButtonText="Назад к продуктам"
        onBackClick={() => navigate("/products")}
      >
        {product.rental?.hasDiscount && (
          <div className="discount-timer">
            <div className="discount-timer__price">
              <span className="discount-timer__price-label">
                Цена со скидкой
              </span>
              <span className="discount-timer__price-value">
                {getDiscountedRentalPriceText(product)}
              </span>
            </div>
            <div className="discount-timer__countdown">
              <span className="discount-timer__time-label">Осталось</span>
              <span className="discount-timer__time-value">
                {timeRemaining}
              </span>
            </div>
          </div>
        )}
      </PageHeader>

      <div className="product-details-content">
        <div className="product-details-grid">
          {/* Main Image Section */}
          <div className="product-image-section">
            <div className="main-image-container">
              {product.mainImage ? (
                <img
                  src={product.mainImage}
                  alt={`${product.brand} ${product.model}`}
                  className="main-image"
                />
              ) : (
                <div className="no-image-placeholder">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21,15 16,10 5,21" />
                  </svg>
                  <p>Изображение не загружено</p>
                </div>
              )}
            </div>

            {/* Additional Images */}
            {product.images && product.images.length > 0 && (
              <div className="additional-images">
                <div className="image-gallery">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${product.brand} ${product.model} - изображение ${
                        index + 1
                      }`}
                      className="gallery-image"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="product-info-section">
            {/* Basic Information */}
            <div className="info-section">
              <h3>
                Основная информация - {product.brand} {product.model}{" "}
                {product.year}
              </h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Лот:</span>
                  <span className="info-value">{product.lotNumber}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Год:</span>
                  <span className="info-value">{product.year}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Пробег:</span>
                  <span className="info-value">
                    {formatMileage(product.mileage)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Объем двигателя:</span>
                  <span className="info-value">{product.engineSize} л</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Тип топлива:</span>
                  <span className="info-value">
                    {product.fuelType === "GASOLINE"
                      ? "Бензин"
                      : product.fuelType === "DIESEL"
                      ? "Дизель"
                      : product.fuelType === "ELECTRIC"
                      ? "Электрический"
                      : product.fuelType === "HYBRID"
                      ? "Гибридный"
                      : product.fuelType === "LPG"
                      ? "Газ"
                      : product.fuelType}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Коробка передач:</span>
                  <span className="info-value">
                    {product.transmission === "MANUAL"
                      ? "МКПП"
                      : product.transmission === "AUTOMATIC"
                      ? "АКПП"
                      : product.transmission === "CVT"
                      ? "Вариатор"
                      : product.transmission === "SEMI_AUTOMATIC"
                      ? "Полуавтомат"
                      : product.transmission}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Привод:</span>
                  <span className="info-value">
                    {product.drivetrain === "FWD"
                      ? "Передний"
                      : product.drivetrain === "RWD"
                      ? "Задний"
                      : product.drivetrain === "AWD"
                      ? "Полный"
                      : product.drivetrain === "4WD"
                      ? "4WD"
                      : product.drivetrain}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Госномер:</span>
                  <span className="info-value">
                    {product.licensePlate || "Не указан"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Цвет:</span>
                  <span className="info-value">
                    {product.color || "Не указан"}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <div className="info-section">
                <h3>Описание</h3>
                <p className="product-description">{product.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
